"use client";

import type React from "react";
import { useState, useRef, useEffect } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { X, ImageIcon, Video, Eraser, CloudUploadIcon } from "lucide-react";
import devDebug from "@/app/lib/utils/dev-debug";
import {
  ALLOWED_IMAGE_TYPES,
  ALLOWED_VIDEO_TYPES,
  getPreviewUrlWithProcessing,
  getMediaDimensions,
  getMediaUploadExec,
  type MediaDimensions,
  getPosterFilename,
  getMediaWatermarked,
} from "@/app/lib/utils/media";
import { MediaType } from "@/app/types/story";
import { createStory } from "@/app/actions/storyActions"; // Import the server action directly
import { getThumbnail } from "@/app/lib/utils/video-thumbnail";
import { fixImageOrientation } from "@/app/lib/utils/image-orientation"; // Import the fixImageOrientation function
import { FILE_MAX_SIZE_SELECTION_MB } from "@/app/lib/utils/media";
import { getPerfilById } from "@/app/actions/perfilActions";

// Variant-specific constants
const VARIANT_CONFIG = {
  story: {
    title: "Adicionar KinkStory",
    allowVideo: true,
    messageEditable: true,
    defaultMessage: "",
    expiresInDays: 14, // Expires in 7 days
    autoUpload: false, // Wait for user to click upload
  },
  avatar: {
    title: "Atualizando foto de perfil ...",
    allowVideo: false,
    messageEditable: false,
    defaultMessage: "Foto de perfil atualizada",
    expiresInDays: 3, // Never expires
    autoUpload: true, // Auto upload after file selection
  },
  gallery: {
    title: "Atualizando galeria ...",
    allowVideo: true,
    messageEditable: false,
    defaultMessage: "Galeria atualizada",
    expiresInDays: 3, // Never expires
    autoUpload: true, // Auto upload after file selection
  },
};

type UploadVariant = keyof typeof VARIANT_CONFIG;

interface StoryUploadProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  session?: any;
  variant?: UploadVariant;
}

const callSilentApi = async (session: any) => {
  // Usando Promise sem await para não bloquear o fluxo principal

  const perfilResult = await getPerfilById(session?.user?.id);

  const url = `/api/og/${encodeURIComponent(
    perfilResult?.perfil.slug || session?.user?.id
  )}`;
  // Chamada GET silenciosa
  fetch(url, {
    method: "GET",
    // Não incluir body em requisições GET
    headers: {
      // Cabeçalhos opcionais, se necessário
      "Cache-Control": "no-cache",
    },
  })
    .then((response) => {
      if (!response.ok) {
        devDebug.log(
          "API silenciosa retornou erro, mas o fluxo principal não é afetado"
        );
      } else {
        devDebug.log("API silenciosa executada com sucesso");
      }
    })
    .catch((error) => {
      // Apenas logamos o erro, sem afetar o fluxo principal
      devDebug.log("Erro na API silenciosa:", error);
    });
};

export default function StoryUpload({
  open,
  onClose,
  onSuccess,
  session,
  variant = "story",
}: StoryUploadProps) {
  const config = VARIANT_CONFIG[variant];

  const [file, setFile] = useState<File | null>(null);
  const [shortMessage, setShortMessage] = useState(config.defaultMessage);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStage, setUploadStage] = useState<string>("Preparando...");
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [mediaDimensions, setMediaDimensions] =
    useState<MediaDimensions | null>(null);
  const [processedFile, setProcessedFile] = useState<File | null>(null);
  const [dialogVisible, setDialogVisible] = useState(false); // Initially hide dialog for all variants
  const [fileSelectionCancelled, setFileSelectionCancelled] = useState(false); // Track if file selection was cancelled
  const [processingComplete, setProcessingComplete] = useState(false); // Track when processing is complete
  const [processingMedia, setProcessingMedia] = useState(false); // Track when media is being processed
  const fileInputRef = useRef<HTMLInputElement>(null);
  const previewContainerRef = useRef<HTMLDivElement>(null);
  const [thumbnailBlob, setThumbnailBlob] = useState<Blob | null>(null);
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [sessionUpdateKey, setSessionUpdateKey] = useState(0);
  const [uploadKey, setUploadKey] = useState(0);

  const handleUploadSuccess = () => {
    setUploadComplete(true);
    setUploadKey((prev) => prev + 1); // Increment key to trigger session update

    // Call the original onSuccess if provided
    if (onSuccess) {
      onSuccess();
    }
  };

  // Open file dialog immediately for all variants when the component is opened
  useEffect(() => {
    if (open && !file && !dialogVisible && !fileSelectionCancelled) {
      // Use a small timeout to ensure the ref is available
      const timer = setTimeout(() => {
        fileInputRef.current?.click();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [open, file, dialogVisible, fileSelectionCancelled]);

  // Auto-upload effect for avatar and gallery variants
  useEffect(() => {
    // Only trigger auto-upload when processing is complete, there's a file, and no errors
    if (
      processingComplete &&
      file &&
      !error &&
      config.autoUpload &&
      !uploading
    ) {
      handleUpload();
      // callSilentApi(session);
    }
  }, [processingComplete, file, error, config.autoUpload, uploading]);

  // Reset state when dialog closes or variant changes
  useEffect(() => {
    if (!open) {
      resetState();
    }
  }, [open, variant]);

  // Update message when variant changes
  useEffect(() => {
    setShortMessage(config.defaultMessage);
  }, [variant, config.defaultMessage]);

  // Clean up object URLs when component unmounts or when they change
  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
      if (processedFile)
        URL.revokeObjectURL(URL.createObjectURL(processedFile));
    };
  }, [preview, processedFile]);

  const resetState = () => {
    setFile(null);
    setProcessedFile(null);
    setShortMessage(config.defaultMessage);
    setError(null);
    setPreview(null);
    setUploadProgress(0);
    setUploadStage("Preparando...");
    setMediaDimensions(null);
    setDialogVisible(false); // Reset dialog visibility
    setFileSelectionCancelled(false); // Reset file selection cancelled flag
    setProcessingComplete(false); // Reset processing complete flag
    setProcessingMedia(false); // Reset processing media flag
    setThumbnailBlob(null);
    setThumbnailFile(null);
  };

  // Helper function to check if a file is an image (including HEIC)
  const isImageFile = (file: File): boolean => {
    // Check by MIME type
    if (file.type.startsWith("image/")) {
      return true;
    }

    // Check by file extension for HEIC/HEIF files
    const fileName = file.name.toLowerCase();
    return (
      fileName.endsWith(".heic") ||
      fileName.endsWith(".heif") ||
      fileName.endsWith(".heics") ||
      file.type === "image/heic" ||
      file.type === "image/heif"
    );
  };

  // Helper function to apply watermark to thumbnail blob
  const applyWatermarkToThumbnail = async (
    blob: Blob,
    filename: string
  ): Promise<File> => {
    // Convert blob to file
    const tempFile = new File([blob], filename, {
      type: blob.type || "image/webp",
    });

    // Apply watermark
    const watermarkedFile = await getMediaWatermarked(tempFile);
    return watermarkedFile;
  };

  // Inside the StoryUpload component, modify the handleFileSelect function:
  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const selectedFile = event.target.files?.[0];

    // Reset the file input value to ensure it triggers again next time
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    if (!selectedFile) {
      // If no file was selected, mark as cancelled and close
      setFileSelectionCancelled(true);
      handleClose();
      return;
    }

    // Reset the cancelled flag since a file was selected
    setFileSelectionCancelled(false);

    // Reset processing complete flag
    setProcessingComplete(false);

    // Show the dialog now that a file has been selected
    setDialogVisible(true);

    // Check if file type is allowed for this variant
    if (!config.allowVideo && !isImageFile(selectedFile)) {
      setError("Apenas imagens são permitidas para este tipo de upload.");
      setFile(selectedFile); // Still set the file so we can show the error with the preview
      setPreview(URL.createObjectURL(selectedFile));
      return;
    }

    try {
      setFile(selectedFile);
      // Start processing indicator
      setProcessingMedia(true);

      // Generate thumbnail AND set it in preview for videos
      if (selectedFile.type.startsWith("video/")) {
        try {
          // Start processing indicator
          setProcessingMedia(true);

          // Use our improved thumbnail generator with fallbacks
          const { blob, isGenerated } = await getThumbnail(selectedFile);
          if (blob) {
            setThumbnailBlob(blob);

            // Apply watermark to thumbnail if this is a story
            const thumbnailToUse = blob;
            let thumbnailFileToUse = null;

            if (variant === "story") {
              try {
                // Apply watermark to the thumbnail
                const watermarkedThumbnailFile =
                  await applyWatermarkToThumbnail(
                    blob,
                    `thumbnail-${selectedFile.name}.webp`
                  );

                // Save the watermarked thumbnail file
                setThumbnailFile(watermarkedThumbnailFile);
                thumbnailFileToUse = watermarkedThumbnailFile;

                // Create a preview URL for the watermarked thumbnail
                const watermarkedPreviewUrl = URL.createObjectURL(
                  watermarkedThumbnailFile
                );
                setPreview(watermarkedPreviewUrl);
              } catch (watermarkErr) {
                devDebug.error(
                  "Error applying watermark to thumbnail:",
                  watermarkErr
                );
                // Fall back to unwatermarked thumbnail
                const thumbnailPreviewUrl = URL.createObjectURL(blob);
                setPreview(thumbnailPreviewUrl);
              }
            } else {
              // For non-story variants, just use the thumbnail as is
              const thumbnailPreviewUrl = URL.createObjectURL(blob);
              setPreview(thumbnailPreviewUrl);
            }

            if (!isGenerated) {
              devDebug.log("Using fallback thumbnail for video");
            }

            // Process the file after setting the thumbnail preview
            const previewResult = await getPreviewUrlWithProcessing(
              selectedFile,
              {
                process: true,
                userId: session?.user?.id,
                maxSizeMB: FILE_MAX_SIZE_SELECTION_MB,
                addWatermark: variant === "story", // Only add watermark for stories
              }
            );

            // Set dimensions if available
            if (previewResult.dimensions) {
              setMediaDimensions(previewResult.dimensions);
            }

            // Set the processed file if available
            if (previewResult.processedFile) {
              setProcessedFile(previewResult.processedFile);
            }

            // Handle validation errors
            if (previewResult.error) {
              setError(previewResult.error);
            } else {
              setError(null);
            }

            // Mark processing as complete
            setProcessingComplete(true);
          }
        } catch (err) {
          devDebug.error("Error generating video thumbnail:", err);

          // Continue with normal processing if thumbnail generation fails
          try {
            const previewResult = await getPreviewUrlWithProcessing(
              selectedFile,
              {
                process: true,
                userId: session?.user?.id,
                maxSizeMB: FILE_MAX_SIZE_SELECTION_MB,
                addWatermark: variant === "story",
              }
            );

            // Set dimensions if available
            if (previewResult.dimensions) {
              setMediaDimensions(previewResult.dimensions);
            }

            // Set the processed file if available
            if (previewResult.processedFile) {
              setProcessedFile(previewResult.processedFile);

              // Create a preview URL for the processed file
              const processedPreviewUrl = URL.createObjectURL(
                previewResult.processedFile
              );
              setPreview(processedPreviewUrl);
            } else {
              // Fallback to original preview if processing fails
              setPreview(URL.createObjectURL(selectedFile));
            }

            // Handle validation errors
            if (previewResult.error) {
              setError(previewResult.error);
            } else {
              setError(null);
            }

            // Mark processing as complete
            setProcessingComplete(true);
          } catch (processErr) {
            devDebug.error("Error processing file:", processErr);
            setError(
              processErr instanceof Error
                ? processErr.message
                : "Erro ao processar arquivo"
            );

            // Fallback to basic preview if processing fails
            setPreview(URL.createObjectURL(selectedFile));

            // Mark processing as complete even if there was an error
            setProcessingComplete(true);
          }
        } finally {
          // Stop processing indicator
          setProcessingMedia(false);
        }
      } else {
        // For images, continue with the existing code
        try {
          // Process the file
          const previewResult = await getPreviewUrlWithProcessing(
            selectedFile,
            {
              process: true,
              userId: session?.user?.id,
              maxSizeMB: FILE_MAX_SIZE_SELECTION_MB,
              addWatermark: variant === "story", // Only add watermark for stories
            }
          );

          // Set dimensions if available
          if (previewResult.dimensions) {
            setMediaDimensions(previewResult.dimensions);
          }

          // Set the processed file if available
          if (previewResult.processedFile) {
            setProcessedFile(previewResult.processedFile);

            // Create a preview URL for the processed file
            let processedPreviewUrl = URL.createObjectURL(
              previewResult.processedFile
            );

            // Fix orientation for JPEG images
            if (selectedFile.type.match(/jpe?g/i)) {
              try {
                processedPreviewUrl = await fixImageOrientation(
                  selectedFile,
                  processedPreviewUrl
                );
              } catch (orientErr) {
                devDebug.error("Error fixing orientation:", orientErr);
              }
            }

            setPreview(processedPreviewUrl);
          } else {
            // Fallback to original preview if processing fails
            let originalPreviewUrl = URL.createObjectURL(selectedFile);

            // Fix orientation for JPEG images
            if (selectedFile.type.match(/jpe?g/i)) {
              try {
                originalPreviewUrl = await fixImageOrientation(
                  selectedFile,
                  originalPreviewUrl
                );
              } catch (orientErr) {
                devDebug.error("Error fixing orientation:", orientErr);
              }
            }

            setPreview(originalPreviewUrl);
          }

          // Handle validation errors
          if (previewResult.error) {
            setError(previewResult.error);
          } else {
            setError(null);
          }

          // Mark processing as complete
          setProcessingComplete(true);
        } catch (err) {
          devDebug.error("Error processing file:", err);
          setError(
            err instanceof Error ? err.message : "Erro ao processar arquivo"
          );

          // Fallback to basic preview if processing fails
          let originalPreviewUrl = URL.createObjectURL(selectedFile);

          // Fix orientation for JPEG images
          if (selectedFile.type.match(/jpe?g/i)) {
            try {
              originalPreviewUrl = await fixImageOrientation(
                selectedFile,
                originalPreviewUrl
              );
            } catch (orientErr) {
              devDebug.error("Error fixing orientation:", orientErr);
            }
          }

          setPreview(originalPreviewUrl);

          // Mark processing as complete even if there was an error
          setProcessingComplete(true);
        } finally {
          // Stop processing indicator
          setProcessingMedia(false);
        }
      }
    } catch (err) {
      devDebug.error("Error processing file:", err);
      setError(
        err instanceof Error ? err.message : "Erro ao processar arquivo"
      );

      // Fallback to basic preview if processing fails
      setFile(selectedFile);

      let originalPreviewUrl = URL.createObjectURL(selectedFile);

      // Fix orientation for JPEG images
      if (selectedFile.type.match(/jpe?g/i)) {
        try {
          originalPreviewUrl = await fixImageOrientation(
            selectedFile,
            originalPreviewUrl
          );
        } catch (orientErr) {
          devDebug.error("Error fixing orientation:", orientErr);
        }
      }

      setPreview(originalPreviewUrl);

      // Try to get dimensions
      try {
        const dimensions = await getMediaDimensions(selectedFile);
        setMediaDimensions(dimensions);
      } catch (dimErr) {
        devDebug.error("Error getting media dimensions:", dimErr);
      }

      // Mark processing as complete even if there was an error
      setProcessingComplete(true);
    } finally {
      // Stop processing indicator
      setProcessingMedia(false);
    }
  };

  // Helper function to update upload stage based on progress
  const updateUploadStage = (progress: number) => {
    if (progress < 4) {
      setUploadStage("Preparando arquivo...");
    } else if (progress < 6) {
      setUploadStage("Verificando o arquivo...");
    } else if (progress < 10) {
      setUploadStage("Obtendo authorização...");
    } else if (progress < 80) {
      setUploadStage("Enviando arquivo...");
    } else if (progress < 100) {
      setUploadStage("Verificando o upload....");
    } else {
      setUploadStage("Upload concluído!");
    }
  };

  // Helper function to calculate expiration date
  const calculateExpirationDate = (days: number): Date | null => {
    if (days <= 0) return null;

    const expirationDate = new Date();
    expirationDate.setDate(expirationDate.getDate() + days);
    return expirationDate;
  };

  const handleUpload = async () => {
    if (!file || !session?.user?.id) return;

    try {
      setUploading(true);
      setError(null);
      setUploadProgress(0);
      setUploadStage("Preparando arquivo...");

      // Use the processed file if available, otherwise use the original file
      const fileToUpload = processedFile || file;

      // Upload the media using our enhanced function with progress tracking
      const result = await getMediaUploadExec(fileToUpload, session.user.id, {
        maxSizeMB: FILE_MAX_SIZE_SELECTION_MB,
        description: shortMessage.trim(),
        onProgress: (progress) => {
          setUploadProgress(progress);
          updateUploadStage(progress);
        },
      });

      if (fileToUpload.type.startsWith("video/")) {
        // Use the watermarked thumbnail file if available, otherwise use the original thumbnail blob
        const thumbToUpload =
          (thumbnailFile &&
            new File([thumbnailFile], getPosterFilename(result.filename), {
              type: "image/webp",
            })) ||
          (thumbnailBlob &&
            new File([thumbnailBlob], getPosterFilename(result.filename), {
              type: "image/webp",
            })) ||
          null;

        if (thumbToUpload) {
          // Upload thumbnail
          const thumbnailResult = await getMediaUploadExec(
            thumbToUpload,
            session.user.id,
            {
              maxSizeMB: 5,
              description: "Thumbnail gerado automaticamente",
            }
          );
        }
      }

      // Create the record with dimensions and size
      setUploadStage("Criando registro...");

      // Calculate expiration date based on the variant configuration
      const expiresAt = calculateExpirationDate(config.expiresInDays);

      try {
        // Call the server action directly instead of going through the API

        createStory({
          mediaFilename: result.filename,
          mediaType: isImageFile(fileToUpload)
            ? MediaType.image
            : MediaType.video,
          shortMessage: shortMessage.trim(),
          mediaWidth: Math.round(result.dimensions?.width || 0),
          mediaHeight: Math.round(result.dimensions?.height || 0),
          mediaSize: Math.round(result.dimensions?.size || 0),
          expiresAt:
            expiresAt || new Date(Date.now() + 1000 * 60 * 60 * 24 * 7),
          variant: variant,
        });

        setUploadProgress(100);
        setUploadStage("Upload concluído!");

        // Success
        onSuccess?.();

        handleClose();
      } catch (err) {
        devDebug.error("Error creating story:", err);
        throw new Error(
          err instanceof Error ? err.message : "Failed to create story"
        );
      }
    } catch (err) {
      devDebug.error("Upload error:", err);
      setError(
        err instanceof Error ? err.message : "Erro ao fazer upload do arquivo"
      );
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleSelectNewFile = () => {
    // Clean up existing preview URL
    if (preview) {
      URL.revokeObjectURL(preview);
    }

    // Reset file-related states but keep the dialog visible
    setFile(null);
    setProcessedFile(null);
    setPreview(null);
    setMediaDimensions(null);
    setError(null);
    setProcessingComplete(false);
    setProcessingMedia(false);
    setThumbnailBlob(null);
    setThumbnailFile(null);

    // Trigger file selection again
    setTimeout(() => {
      fileInputRef.current?.click();
    }, 100);
  };

  // Common styles for the media container (both drag area and preview)
  const mediaContainerStyles = {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    flexGrow: 1,
    // width: "100%",
    height: "100%",
    minHeight: "300px",
    p: 0,
    // mb: 2,
    borderRadius: 2,
  };

  // Determine allowed file types based on variant
  const allowedFileTypes = config.allowVideo
    ? [...ALLOWED_IMAGE_TYPES, ...ALLOWED_VIDEO_TYPES]
    : ALLOWED_IMAGE_TYPES;

  // Always render the file input to handle the initial file selection
  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept={allowedFileTypes.join(",")}
        style={{ display: "none" }}
      />

      {dialogVisible && (
        <Dialog
          open={open}
          onClose={handleClose}
          fullWidth
          fullScreen
          PaperProps={{
            sx: {
              borderRadius: 2,
              maxWidth: "sm",
            },
          }}
        >
          <DialogTitle
            sx={{
              m: 0,
              py: 0.5,
              px: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="body1" color="primary" fontWeight={"bold"}>
              {config.title}
            </Typography>
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={{ color: "grey.500" }}
            >
              <X />
            </IconButton>
          </DialogTitle>

          <DialogContent
            dividers
            sx={{
              p: 2,
              display: "flex",
              flexDirection: "column",
              height: "calc(100vh - 120px)",
            }}
          >
            {error && (
              <Alert
                severity="error"
                onClose={() => setError(null)}
                sx={{ mb: 2 }}
              >
                {error}
              </Alert>
            )}

            {!file ? (
              // Show file selection UI when no file is selected but dialog is visible
              <Box
                sx={{
                  ...mediaContainerStyles,
                  border: "2px dashed",
                  borderColor: "divider",
                  p: 3,
                  textAlign: "center",
                  cursor: "pointer",
                  "&:hover": {
                    borderColor: "primary.main",
                    bgcolor: "action.hover",
                  },
                }}
                onClick={() => fileInputRef.current?.click()}
              >
                <CloudUploadIcon size={50} color="white" />
                <Typography variant="h6" gutterBottom>
                  Clique para selecionar
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  ou arraste um arquivo aqui
                </Typography>
                <Box
                  sx={{
                    mt: 2,
                    display: "flex",
                    gap: 3,
                    justifyContent: "center",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    <ImageIcon size={24} />
                    <Typography variant="caption">Imagens</Typography>
                  </Box>
                  {config.allowVideo && (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <Video size={24} />
                      <Typography variant="caption">Vídeos</Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            ) : (
              // Show preview when file is selected
              <Box
                ref={previewContainerRef}
                sx={{
                  ...mediaContainerStyles,
                  bgcolor: "black",
                  overflow: "hidden",
                }}
              >
                {processingMedia ? (
                  // Show loading indicator while processing
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    <CircularProgress color="primary" size={60} />
                    <Typography variant="body2" color="white">
                      Processando mídia...
                    </Typography>
                  </Box>
                ) : (
                  // Show preview when processing is complete
                  preview && (
                    <>
                      {isImageFile(file) ? (
                        // For images, just show the image
                        <Box
                          component="img"
                          src={preview}
                          alt="Preview"
                          sx={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            objectFit: "contain",
                            borderRadius: 1,
                          }}
                        />
                      ) : (
                        // For videos, show either the thumbnail or the video player
                        file.type.startsWith("video/") && (
                          <Box
                            sx={{
                              position: "relative",
                              width: "100%",
                              height: "100%",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            {/* Video element for playback */}
                            <Box
                              component="video"
                              src={
                                processedFile
                                  ? URL.createObjectURL(processedFile)
                                  : URL.createObjectURL(file)
                              }
                              sx={{
                                maxWidth: "100%",
                                maxHeight: "100%",
                                objectFit: "contain",
                                borderRadius: 1,
                                display: "block",
                              }}
                              controls
                              controlsList="nodownload nofullscreen noremoteplayback nopictureinpicture"
                              playsInline
                              webkit-playsinline=""
                              poster={preview}
                            />
                          </Box>
                        )
                      )}
                      <Box
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          display: "flex",
                          gap: 1,
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={handleSelectNewFile}
                          sx={{
                            bgcolor: "rgba(0, 0, 0, 0.5)",
                            color: "white",
                            "&:hover": {
                              bgcolor: "rgba(0, 0, 0, 0.7)",
                            },
                          }}
                        >
                          <Eraser size={19} color="orange" />
                        </IconButton>
                      </Box>
                    </>
                  )
                )}
              </Box>
            )}

            <TextField
              label="Mensagem"
              value={shortMessage}
              sx={{
                display: { xs: !config.autoUpload ? "flex" : "none" },
                width: "100%",
                mt: 2,
              }}
              onChange={(e) => {
                if (config.messageEditable) {
                  const value = e.target.value.slice(0, 50);
                  setShortMessage(value);
                }
              }}
              inputProps={{
                maxLength: 50,
              }}
              helperText={`${shortMessage.length}/50 caracteres`}
            />

            {uploading && (
              <Box sx={{ width: "100%", mt: 2 }}>
                <LinearProgress variant="determinate" value={uploadProgress} />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mt: 1,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {uploadStage}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {Math.round(uploadProgress)}%
                  </Typography>
                </Box>
              </Box>
            )}
          </DialogContent>

          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={handleClose}
              disabled={uploading || processingMedia}
            >
              Cancelar
            </Button>
            {!config.autoUpload && (
              <Button
                onClick={handleUpload}
                variant="contained"
                disabled={!file || uploading || processingMedia || !!error}
                startIcon={<CloudUploadIcon />}
              >
                {uploading ? "Enviando..." : "Enviar"}
              </Button>
            )}
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}
